import { Observable } from 'rxjs';

import { OnDisconnect } from '@angular/fire/database';
import { ListenEvent } from '@angular/fire/database';

export type DatabaseListPayload<T> = { val: () => T };
export type DatabaseObjectPayload<T> = { key: string, val: () => T };

// Query from the old compat interface (we still use this interface around in the app, however it is converted to the new in the fire DB)
interface Query {
    endAt(value: number | string | boolean | null, key?: string): Query;
    endBefore(value: number | string | boolean | null, key?: string): Query;
    equalTo(value: number | string | boolean | null, key?: string): Query;
    isEqual(other: Query | null): boolean;
    limitToFirst(limit: number): Query;
    limitToLast(limit: number): Query;
    orderByChild(path: string): Query;
    orderByKey(): Query;
    orderByPriority(): Query;
    orderByValue(): Query;
    startAt(value: number | string | boolean | null, key?: string): Query;
    startAfter(value: number | string | boolean | null, key?: string): Query;
}

// Export types to avoid direct reference to Firebase database // DatabaseReference
export type QueryFn = (ref: Query) => Query;
export { ListenEvent } ;

export interface AbstractDatabase {

    /**
    * Instance of database with ref for write operations
    */
    database: {
        ref<T>(string): DatabaseRef<T>
    };

    /**
    * Init database and its offline cache
    */
    init(enableDbCache: boolean, offlineContentCache?: string): Promise<any>;

    /**
    * Get database list reference with offline capabilities
    * @param ref Database list reference
    * @param query Database query
    */
    list<T>(ref: string, query?: any): ListRef<T>;

    /**
    * Get a database object reference with offline capbilities
    * @param ref Database reference string
    */
    object<T>(ref: string): ObjectRef<T>

    /**
    * Enable or disable offline mode
    * In offline mode the database will only return read results from the offline cache 
    * or empty results if no cache is available. Offline reads will never be queried on Firebase.
    * This will also enable/disable the Firebase offline persistance mode, which enables queuing up write operations.
    */
    enableOfflineMode(enable: boolean);

    /**
    * Detect if network is connected or not according to firebase info
    */
    isNetworkConnected(): Promise<boolean>;

    /**
    * Watch network connection (to database)
    */
    watchNetworkConnected(): Observable<boolean>;
}

export interface DatabaseRef<T> {
    onDisconnect: () => OnDisconnect;
    once: (value: string) => Promise<any>;
    set(data?: any): Promise<void>;
    update(data: any): Promise<any>;
    push(): { key: string };
    remove(): Promise<any>;
}

export interface ObjectRef<T> {
    valueChanges(events?: any[]): Observable<T>;
    snapshotChanges?(events?: any[]): Observable<{ payload: DatabaseObjectPayload<T>, key: string }>;

    set(data?: any): Promise<void>;
    update(data: any): Promise<any>;
    remove(): Promise<any>;
}

export interface ListRef<T> {
    valueChanges(events?: any[]): Observable<T[]>;
    snapshotChanges(events?: any[]): Observable<{ type: string, payload: DatabaseListPayload<T>, key: string }[]>;

    push(data?: any): { key: string };
    update(item: any, data: T): Promise<any>;
    set(item: any, data: T): Promise<void>;
    remove(item?: any): Promise<any>;
}
